import React from "react";

import { useGetFootprintingCropFootprint, useGetPracticeCrops, useTranslation } from "utils";

import { FormFlowOverlay, FormPage as FormFlowStep } from "components";

import { CropDryingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropDryingForm";
import { CropFertilizersForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFertilizersForm";
import { CropFieldSelectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFieldSelectionForm";
import { CropGeneralForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGeneralForm";
import { CropIrrigationForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropIrrigationForm";
import { CropPlantProtectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropPlantProtectionForm";
import { CropTillageAndSeedingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTillageAndSeedingForm";
import { CropTransportForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTransportForm";

interface CropFlowProps {
  showFormFlow: boolean;
  onExit: () => void;
  farmId: string;
  cropId: string;
  year: number;
  readOnly?: boolean;
  initialStep?: number;
  editMode?: boolean;
}

const cropFormCategories = {
  fieldWork: "footprintingLandingPage.assessmentCategories.crop.fieldWork",
};

export const CropFormFlow = ({ onExit, showFormFlow, farmId, cropId, year, readOnly, initialStep, editMode }: CropFlowProps) => {
  const { t } = useTranslation("footprinting");
  const cropProgressQuery = useGetFootprintingCropFootprint(farmId, cropId, year);
  const { data: allCrops } = useGetPracticeCrops({ enabled: true });

  const cropName = allCrops?.find((crop) => crop.id === cropId)?.name;

  const cropFlowSteps: FormFlowStep[] = [
    {
      title: t("navigation.crop.general"),
      body: () => <CropGeneralForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
    },
    {
      title: t("navigation.crop.fields"),
      body: () => (
        <CropFieldSelectionForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />
      ),
    },
    {
      title: t("navigation.crop.tillageAndSeeding"),
      category: t(cropFormCategories.fieldWork),
      body: () => (
        <CropTillageAndSeedingForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />
      ),
    },
    {
      title: t("navigation.crop.fertilizers"),
      category: t(cropFormCategories.fieldWork),
      body: () => <CropFertilizersForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
    },
    {
      title: t("navigation.crop.plantProtection"),
      category: t(cropFormCategories.fieldWork),
      body: () => (
        <CropPlantProtectionForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />
      ),
    },
    {
      title: t("navigation.crop.irrigation"),
      body: () => <CropIrrigationForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
    },
    {
      title: t("navigation.crop.drying"),
      body: () => <CropDryingForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} cropId={cropId} farmId={farmId} year={year} />,
    },
    {
      title: t("navigation.crop.transport"),
      body: () => <CropTransportForm readOnly={readOnly} primaryDataId={cropProgressQuery.data?.footprint?.id} farmId={farmId} cropId={cropId} year={year} />,
    },
  ];

  return (
    <FormFlowOverlay
      onFormClose={onExit}
      showModal={showFormFlow}
      formPages={cropFlowSteps}
      initialStep={initialStep}
      completionPercent={cropProgressQuery.data?.completionPct ?? 0}
      getPageStatus={(pageNumber) => {
        if (cropProgressQuery.data?.pagesCompleted.includes(pageNumber)) return "complete";
        if (cropProgressQuery.data?.pagesIncomplete.includes(pageNumber)) return "incomplete";
        return null;
      }}
      // {/* TODO: Use translated crop names based on HF work */}
      navBarTitle={t("navigation.crop.navBarTitle", { cropName, year })}
      dataTestId="crop-form-flow-overlay"
      editMode={editMode}
    />
  );
};
